import React from "react";
import { CheckCircle2, AlertTriangle, XCircle, HelpCircle, Flame, Lock } from "lucide-react";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { cn } from "../lib/utils";

function statusMeta(status) {
  switch (status) {
    case "pass":
      return { icon: CheckCircle2, variant: "success", text: "Pass" };
    case "fail":
      return { icon: XCircle, variant: "danger", text: "Fail" };
    case "warn":
      return { icon: AlertTriangle, variant: "warning", text: "Warn" };
    default:
      return { icon: HelpCircle, variant: "muted", text: "Unknown" };
  }
}

function LpBurnBar({ check }) {
  if (check.id !== "lp_burn") return null;
  const burned = typeof check.burned_pct === "number" ? check.burned_pct : null;
  const locked = typeof check.locked_pct === "number" ? check.locked_pct : null;
  const unlocked = typeof check.unlocked_pct === "number" ? check.unlocked_pct : null;
  if (burned === null || locked === null || unlocked === null) return null;
  const total = burned + locked + unlocked;
  const scale = total > 0 ? total : 1;
  const burnedW = (burned / scale) * 100;
  const lockedW = (locked / scale) * 100;
  const unlockedW = (unlocked / scale) * 100;
  return (
    <div className="mt-2">
      <div className="flex h-2 w-full overflow-hidden rounded-full bg-[hsl(var(--muted)/0.15)]">
        <div
          className="bg-[hsl(var(--success))] transition-all"
          style={{ width: `${burnedW}%` }}
          title={`${burned.toFixed(1)}% burned`}
        />
        <div
          className="bg-[hsl(var(--primary))] transition-all"
          style={{ width: `${lockedW}%` }}
          title={`${locked.toFixed(1)}% locked`}
        />
        <div
          className="bg-[hsl(var(--danger))] transition-all"
          style={{ width: `${unlockedW}%` }}
          title={`${unlocked.toFixed(1)}% unlocked`}
        />
      </div>
      <div className="mt-1 flex items-center flex-wrap gap-2 text-[11px]">
        <span className="inline-flex items-center gap-1">
          <Flame className="h-3 w-3 text-[hsl(var(--success))]" />
          <span className="text-[hsl(var(--muted))]">Burned</span>
          <span className="font-medium">{burned.toFixed(1)}%</span>
        </span>
        <span className="inline-flex items-center gap-1">
          <Lock className="h-3 w-3 text-[hsl(var(--primary))]" />
          <span className="text-[hsl(var(--muted))]">Locked</span>
          <span className="font-medium">{locked.toFixed(1)}%</span>
          {check.locker && (
            <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0 h-4 min-w-0">
              {check.locker}
            </Badge>
          )}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="text-[hsl(var(--muted))]">Unlocked</span>
          <span className="font-medium">{unlocked.toFixed(1)}%</span>
        </span>
      </div>
    </div>
  );
}

export function RiskCheckCard({ check }) {
  const meta = statusMeta(check.status);
  const Icon = meta.icon;
  return (
    <Card
      className={cn(
        "p-4 sm:p-5 flex flex-col gap-2 hover:border-[hsl(var(--primary)/0.4)] transition-colors"
      )}
      data-testid="risk-check-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon
            size={18}
            className={cn(
              meta.variant === "success" && "text-[hsl(var(--success))]",
              meta.variant === "warning" && "text-[hsl(var(--warning))]",
              meta.variant === "danger" && "text-[hsl(var(--danger))]",
              meta.variant === "muted" && "text-[hsl(var(--muted))]"
            )}
          />
          <h4 className="text-sm font-semibold tracking-wide">{check.label}</h4>
        </div>
        <Badge variant={meta.variant}>{meta.text}</Badge>
      </div>
      <p className="text-xs text-[hsl(var(--muted))] leading-relaxed">
        {check.detail}
      </p>
      <LpBurnBar check={check} />
    </Card>
  );
}
